"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Compass, ShieldCheck, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const founders = [{
  name: "Scott",
  title: "Aesthetic Direction & Storytelling",
  focus: "Cultural immersion, private villa atmosphere, and bespoke dining",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796755/img/ey-scott-leete-v2.webp",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796755/img/ey-scott-leete-v2.webp-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796755/img/ey-scott-leete-v2.webp-wvc-sizes",
  alt: "Scott curating custom lookbooks and cultural itineraries for families",
  icon: Sparkles,
  tag: "Aesthetic Harmony"
}, {
  name: "Michael",
  title: "Logistical Matrix & Operations",
  focus: "Risk mitigation, private aviation links, and supplier negotiations",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796755/img/Michael_photo.jpg",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796755/img/Michael_photo.jpg-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796755/img/Michael_photo.jpg-wvc-sizes",
  alt: "Michael reviewing complex family travel logistical matrices and schedules",
  icon: ShieldCheck,
  tag: "Logistical Precision"
}];
const pillars = [{
  title: "Double the oversight",
  desc: "Two travel designers review every transfer, dining reservation, and private charter before you ever pack a bag."
}, {
  title: "Balanced generational interests",
  desc: "Logistics structured so grandparents enjoy quiet unhurried mornings while teenagers embark on guided excursions."
}, {
  title: "Zero friction execution",
  desc: "Pre-vetted local fixers and direct supplier contacts ready to adapt schedules if family energy shifts."
}];
export default function Copilot() {
  const shouldReduceMotion = useReducedMotion();
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="co-pilot-introduction" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden border-b border-border/60">
      {/* Subtle decorative hairline orbit ring */}
      <div className="pointer-events-none absolute -top-24 right-10 size-96 rounded-full border border-border/40 opacity-40 lg:block hidden" aria-hidden="true" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          
          {/* Left Column: Asymmetrical Side-by-Side Founder Portraits */}
          <motion.div className="lg:col-span-7 flex flex-col gap-6" initial="hidden" whileInView="show" viewport={{
          once: true,
          amount: 0.2
        }} variants={fadeIn}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-4 lg:gap-6">
              {founders.map((founder, i) => {
              const IconComponent = founder.icon;
              return <div key={founder.name} data-index={i} className="group relative flex flex-col bg-card border border-border/80 rounded-sm overflow-hidden shadow-xs hover:border-primary/40 transition-[border-color,box-shadow] duration-300">
                    {/* Portrait container */}
                    <div className="relative aspect-[4/5] overflow-hidden bg-muted">
                      <img src={founder.image} data-wvc-srcset={founder.imageSrcset} data-wvc-sizes={founder.imageSizes} alt={founder.alt} loading="lazy" decoding="async" className="w-full h-full object-cover object-center grayscale-[15%] group-hover:scale-[1.02] transition-transform duration-500 ease-out" />
                      <div className="absolute top-3 left-3 bg-background/90 backdrop-blur-xs border border-border/70 px-2.5 py-1 rounded-sm text-[0.6875rem] font-medium tracking-widest uppercase text-foreground flex items-center gap-1.5">
                        <IconComponent className="size-3 text-primary" />
                        <span>{founder.tag}</span>
                      </div>
                    </div>

                    {/* Card Content */}
                    <div className="p-5 flex flex-col justify-between flex-1 bg-card">
                      <div>
                        <h3 className="font-serif text-xl text-foreground font-medium">
                          {founder.name}
                        </h3>
                        <p className="text-xs uppercase tracking-wider text-muted-foreground mt-0.5 font-medium">
                          {founder.title}
                        </p>
                      </div>
                      <p className="text-xs text-foreground/80 leading-relaxed mt-4 pt-3 border-t border-border/50">
                        {founder.focus}
                      </p>
                    </div>
                  </div>;
            })}
            </div>

            {/* Micro Sterling Quote Banner */}
            <div className="bg-muted/60 border border-border/70 p-4 sm:p-5 rounded-sm flex items-center gap-4">
              <div className="size-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0 text-primary">
                <Compass className="size-4" />
              </div>
              <p className="text-xs sm:text-sm text-foreground/90 font-serif italic leading-snug">
                {"\"No single detail escapes two pairs of eyes calibrated to separate disciplines.\""}
              </p>
            </div>
          </motion.div>

          {/* Right Column: Editorial Narrative & Value Proposition */}
          <motion.div className="lg:col-span-5 flex flex-col justify-center pt-2 lg:pt-4" initial="hidden" whileInView="show" viewport={{
          once: true,
          amount: 0.2
        }} variants={fadeIn}>
            {/* Eyebrow */}
            <div className="inline-flex items-center gap-2 mb-3">
              <span className="h-px w-6 bg-primary" />
              <span className="text-xs uppercase tracking-[0.15em] font-semibold text-primary">
                The Co-Pilot Model
              </span>
            </div>

            {/* Headline */}
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-[2.65rem] leading-[1.18] text-foreground font-medium tracking-tight">
              Two perspectives. One effortless family journey.
            </h2>

            {/* Editorial Lead Paragraph */}
            <p className="mt-5 text-foreground/90 text-sm sm:text-base leading-relaxed">
              Multi-generational travel brings competing tempos, diverse physical needs, and distinct generational expectations. Managing it requires both unyielding operational precision and deep aesthetic empathy.
            </p>

            <p className="mt-3 text-muted-foreground text-xs sm:text-sm leading-relaxed">
              Rather than delegating your milestone to an anonymous support desk, Scott and Michael personally steward your journey from the initial family alignment call to your return flight home.
            </p>

            {/* Three Foundation Points */}
            <div className="mt-8 space-y-4 border-t border-border/70 pt-6">
              {pillars.map((pillar, i) => <div key={pillar.title} data-index={i} className="flex items-start gap-3.5">
                  <div className="mt-1 size-1.5 rounded-full bg-primary shrink-0" />
                  <div>
                    <h4 className="text-sm font-semibold text-foreground tracking-normal font-sans">
                      {pillar.title}
                    </h4>
                    <p className="text-xs text-muted-foreground leading-relaxed mt-0.5">
                      {pillar.desc}
                    </p>
                  </div>
                </div>)}
            </div>

            {/* Actions */}
            <div className="mt-8 pt-4 flex flex-col sm:flex-row items-stretch sm:flex-row sm:items-center gap-3.5">
              <Button asChild size="default" className="rounded-xs uppercase tracking-wider text-xs px-6 py-2.5">
                <Link to="/FoundersPage">
                  Meet the Founders
                  <ArrowRight className="size-3.5" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="default" className="rounded-xs text-xs uppercase tracking-wider px-5 py-2.5 border-foreground/30 text-foreground hover:bg-foreground/5">
                <Link to="/AnchorOrbitMethodPage">
                  Explore Our Method
                </Link>
              </Button>
            </div>
          </motion.div>

        </div>
      </div>
    </section>;
}